// TelcoTrack — canonical Zakázky shared-drive layout (one polygon = one folder).
// TelcoTrack is the source of truth; Drive is a projection. App never deletes user files.

import type { PolygonResponsibleBranch } from './polygon-business.constants';
import { POLYGON_RESPONSIBLE_BRANCHES } from './polygon-business.constants';

export const PROJECT_DRIVE_SCHEMA_VERSION = 7 as const;

/** Fallback when tenant `driveCustomerFolder` is unset (legacy deployments). */
export const PROJECT_DRIVE_CUSTOMER_DEFAULT = 'CETIN';

/** @deprecated Use {@link PROJECT_DRIVE_CUSTOMER_DEFAULT} or tenant `driveCustomerFolder`. */
export const PROJECT_DRIVE_CUSTOMER = PROJECT_DRIVE_CUSTOMER_DEFAULT;

export function projectDriveInvestorFolderName(
  configured: string | null | undefined,
  fallback: string = PROJECT_DRIVE_CUSTOMER_DEFAULT,
): string {
  const trimmed = (configured ?? '').trim();
  return trimmed || fallback;
}

export const PROJECT_DRIVE_ROOT_OVERVIEWS = '00_PŘEHLEDY_APP';
export const PROJECT_DRIVE_ROOT_BUILDINGS = '01_STAVBY';
export const PROJECT_DRIVE_ROOT_ARCHIVE = '90_ARCHIV';

export const PROJECT_DRIVE_ACTIVE_BUILDINGS_FILE = 'Aktivní_stavby.xlsx';
export const PROJECT_DRIVE_WF_FILE = 'Tabulka_WF.xlsx';
export const PROJECT_DRIVE_WORK_REPORT_FILE = 'Výkaz_práce.xlsx';
/** Stem files in `05_D5_a_schémata_vláken/`. Per-cabinet names use {@link projectDriveOccupancySchemaFileName}. */
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_XLSX = 'D5.xlsx';
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_PDF = 'D5.pdf';
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_XLSX_ARTIFACT = 'occupancy_schema_xlsx';
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_PDF_ARTIFACT = 'occupancy_schema_pdf';
/** Same D5 files in `01_ZADÁNÍ_A_PD/03_PD_aktuální/D5` — next to other object drawings. */
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_PD_XLSX_ARTIFACT = 'occupancy_schema_xlsx_pd';
export const PROJECT_DRIVE_OCCUPANCY_SCHEMA_PD_PDF_ARTIFACT = 'occupancy_schema_pdf_pd';
export const PROJECT_DRIVE_INFO_FILE = 'INFORMACE — SOUBORY ŘÍZENÉ APLIKACÍ.txt';

export const PROJECT_DRIVE_INFO_TEXT =
  'Soubory v 00_APP_PŘEHLEDY a další automatické výstupy TelcoTracku se přepisují z aplikace.\n' +
  'Údaje upravujte v TelcoTracku, ne ručně v těchto souborech.\n';

export const PROJECT_DRIVE_SYNC_STATUSES = [
  'pending_identity',
  'queued',
  'synced',
  'error',
] as const;
export type ProjectDriveSyncStatus = (typeof PROJECT_DRIVE_SYNC_STATUSES)[number];

/** Zodpovědná pobočka → složka pod CETIN/{rok}/. */
export const PROJECT_DRIVE_BRANCH_FOLDERS = {
  'ProTelco Lovosice': '01_LOVOSICE',
  'Fiber Beta': '02_BETA_FIBER',
  EXTERMIS: '03_EXTERMIS',
  'ProTelco Ostrava': '04_OSTRAVA',
  'ProTelco Pardubice': '05_PARDUBICE',
} as const satisfies Record<PolygonResponsibleBranch, string>;

export type ProjectDriveArtifactFolderKey =
  | 'appOverview'
  | 'assignmentRoot'
  | 'sd'
  | 'ppd'
  | 'pdCurrent'
  | 'pdC1'
  | 'pdC2'
  | 'pdC3'
  | 'pdD1'
  | 'pdD2'
  | 'pdD3'
  | 'pdD4'
  | 'pdD5'
  /** PD Technická zpráva (Úřady filing `tz`) — sibling of C1–D5 under `03_PD_aktuální`. */
  | 'pdTz'
  | 'pdPrevious'
  | 'pdChanges'
  | 'permitsRoot'
  | 'statementsIs'
  | 'doss'
  | 'dio'
  | 'excavationPermits'
  | 'acquisition'
  | 'digitalPath'
  | 'evbr'
  | 'posta'
  | 'realizationRoot'
  | 'progress'
  | 'progressConstruction'
  | 'progressOptics'
  | 'progressVr'
  | 'diary'
  | 'photos'
  | 'preBackfillChecks'
  | 'surfaceHandover'
  | 'responsibilityHandover'
  | 'koncovyBodZafuku'
  | 'deviations'
  | 'opticsRoot'
  | 'blowIn'
  | 'welds'
  | 'litFiber'
  | 'montage'
  | 'occupancySchema'
  | 'measurements'
  | 'materialRoot'
  | 'limitky'
  | 'deliveryNotes'
  | 'weighTickets'
  | 'thirdPartyInvoices'
  | 'ppdActuals'
  | 'scheduleRoot'
  | 'harmonogram'
  | 'workPlans'
  | 'contacts'
  | 'notes'
  | 'dspsRoot'
  | 'dspsWorking'
  | 'dspsFinal'
  | 'litFiberHandover'
  | 'handoverProtocols'
  | 'pasportizace'
  | 'otherUser';

/**
 * CETIN PD drawing codes as children of `03_PD_aktuální`.
 * Product evidence: C2 polohopis PDFs, D1 CAD layers, D4 rozvláknění, D5 schéma osazení.
 * C1–C3 and D1–D5 are the usual PD set; folder names stay the codes (no invented titles).
 */
export const PROJECT_DRIVE_PD_DRAWING_CODES = [
  'C1',
  'C2',
  'C3',
  'D1',
  'D2',
  'D3',
  'D4',
  'D5',
] as const;
export type ProjectDrivePdDrawingCode = (typeof PROJECT_DRIVE_PD_DRAWING_CODES)[number];
export type ProjectDrivePdDrawingFolderKey = `pd${ProjectDrivePdDrawingCode}`;

const PROJECT_DRIVE_PD_DRAWING_SKELETON: ReadonlyArray<{
  key: ProjectDrivePdDrawingFolderKey;
  segments: readonly string[];
}> = PROJECT_DRIVE_PD_DRAWING_CODES.map((code) => ({
  key: `pd${code}` as ProjectDrivePdDrawingFolderKey,
  segments: ['01_ZADÁNÍ_A_PD', '03_PD_aktuální', code],
}));

/** Stable 1st/2nd-level skeleton created on bootstrap. Leaf address/phase/day folders are on-demand. */
export const PROJECT_DRIVE_SKELETON: ReadonlyArray<{
  key: ProjectDriveArtifactFolderKey;
  segments: readonly string[];
}> = [
  { key: 'appOverview', segments: ['00_APP_PŘEHLEDY'] },
  { key: 'assignmentRoot', segments: ['01_ZADÁNÍ_A_PD'] },
  { key: 'sd', segments: ['01_ZADÁNÍ_A_PD', '01_SD_startovací_dokumentace'] },
  { key: 'ppd', segments: ['01_ZADÁNÍ_A_PD', '02_PPD_objednávka_a_rozpočet'] },
  { key: 'pdCurrent', segments: ['01_ZADÁNÍ_A_PD', '03_PD_aktuální'] },
  ...PROJECT_DRIVE_PD_DRAWING_SKELETON,
  /** Filing slot Technická zpráva — same level as C1–D5 (legacy zakázky / Úřady `tz`). */
  { key: 'pdTz', segments: ['01_ZADÁNÍ_A_PD', '03_PD_aktuální', 'TZ'] },
  { key: 'pdPrevious', segments: ['01_ZADÁNÍ_A_PD', '90_PD_předchozí_verze'] },
  { key: 'pdChanges', segments: ['01_ZADÁNÍ_A_PD', '99_Změny_oproti_PD'] },
  { key: 'permitsRoot', segments: ['02_POVOLENÍ_A_AKVIZICE'] },
  { key: 'statementsIs', segments: ['02_POVOLENÍ_A_AKVIZICE', '01_Stanoviska_IS'] },
  { key: 'doss', segments: ['02_POVOLENÍ_A_AKVIZICE', '02_DOSS'] },
  { key: 'dio', segments: ['02_POVOLENÍ_A_AKVIZICE', '03_DIO'] },
  { key: 'excavationPermits', segments: ['02_POVOLENÍ_A_AKVIZICE', '04_Výkopová_povolení_a_vytyčení'] },
  { key: 'acquisition', segments: ['02_POVOLENÍ_A_AKVIZICE', '05_Akvizice_a_souhlasy'] },
  { key: 'digitalPath', segments: ['02_POVOLENÍ_A_AKVIZICE', '06_Digitální_cesta'] },
  { key: 'evbr', segments: ['02_POVOLENÍ_A_AKVIZICE', '07_eVBř'] },
  { key: 'posta', segments: ['02_POVOLENÍ_A_AKVIZICE', '08_Komunikace'] },
  { key: 'realizationRoot', segments: ['03_REALIZACE'] },
  { key: 'progress', segments: ['03_REALIZACE', '01_Rozpracovanost'] },
  { key: 'progressConstruction', segments: ['03_REALIZACE', '01_Rozpracovanost', 'Stavební'] },
  { key: 'progressOptics', segments: ['03_REALIZACE', '01_Rozpracovanost', 'Optika'] },
  { key: 'progressVr', segments: ['03_REALIZACE', '01_Rozpracovanost', 'VR'] },
  { key: 'diary', segments: ['03_REALIZACE', '02_Stavební_deník'] },
  { key: 'photos', segments: ['03_REALIZACE', '03_Fotodokumentace'] },
  { key: 'preBackfillChecks', segments: ['03_REALIZACE', '04_Kontroly_před_záhozem'] },
  { key: 'surfaceHandover', segments: ['03_REALIZACE', '05_Předání_povrchů'] },
  { key: 'responsibilityHandover', segments: ['03_REALIZACE', '08_Předání_odpovědnosti'] },
  { key: 'koncovyBodZafuku', segments: ['03_REALIZACE', '07_Koncový_bod_záfuku'] },
  { key: 'deviations', segments: ['03_REALIZACE', '06_Neshody_a_změny'] },
  { key: 'opticsRoot', segments: ['04_OPTIKA_A_VR'] },
  { key: 'blowIn', segments: ['04_OPTIKA_A_VR', '01_Záfuk'] },
  { key: 'welds', segments: ['04_OPTIKA_A_VR', '02_Sváry_a_tabulky_provaření'] },
  { key: 'litFiber', segments: ['04_OPTIKA_A_VR', '03_Svítící_vlákno'] },
  { key: 'montage', segments: ['04_OPTIKA_A_VR', '04_Montážní_listy'] },
  { key: 'occupancySchema', segments: ['04_OPTIKA_A_VR', '05_D5_a_schémata_vláken'] },
  { key: 'measurements', segments: ['04_OPTIKA_A_VR', '06_Měření_OK_a_OTDR'] },
  { key: 'materialRoot', segments: ['05_MATERIÁL_A_NÁKLADY'] },
  { key: 'limitky', segments: ['05_MATERIÁL_A_NÁKLADY', '01_Limitky_a_výdejky'] },
  { key: 'deliveryNotes', segments: ['05_MATERIÁL_A_NÁKLADY', '02_Dodací_listy'] },
  { key: 'weighTickets', segments: ['05_MATERIÁL_A_NÁKLADY', '03_Vážní_lístky'] },
  { key: 'thirdPartyInvoices', segments: ['05_MATERIÁL_A_NÁKLADY', '04_Faktury_třetích_stran'] },
  { key: 'ppdActuals', segments: ['05_MATERIÁL_A_NÁKLADY', '05_PPD_a_skutečné_náklady'] },
  { key: 'scheduleRoot', segments: ['06_HARMONOGRAM_A_KOORDINACE'] },
  { key: 'harmonogram', segments: ['06_HARMONOGRAM_A_KOORDINACE', '01_Harmonogram'] },
  { key: 'workPlans', segments: ['06_HARMONOGRAM_A_KOORDINACE', '02_Plány_prací'] },
  { key: 'contacts', segments: ['06_HARMONOGRAM_A_KOORDINACE', '03_Kontakty_a_subdodavatelé'] },
  { key: 'notes', segments: ['06_HARMONOGRAM_A_KOORDINACE', '04_Zápisy_a_sdílené_poznámky'] },
  { key: 'dspsRoot', segments: ['07_DSPS_A_PŘEDÁNÍ'] },
  { key: 'dspsWorking', segments: ['07_DSPS_A_PŘEDÁNÍ', '01_Pracovní_DSPS'] },
  { key: 'dspsFinal', segments: ['07_DSPS_A_PŘEDÁNÍ', '02_Finální_DSPS'] },
  { key: 'litFiberHandover', segments: ['07_DSPS_A_PŘEDÁNÍ', '03_Dílčí_předání_svítícího_vlákna'] },
  { key: 'handoverProtocols', segments: ['07_DSPS_A_PŘEDÁNÍ', '04_Předávací_protokoly'] },
  /** Upload target for as-built / asset passport files (pasportizace). */
  { key: 'pasportizace', segments: ['08_PASPORTIZACE'] },
  { key: 'otherUser', segments: ['90_OSTATNÍ_UŽIVATELSKÉ'] },
];

export const PROJECT_DRIVE_APP_PROPERTIES = {
  managedBy: 'telcotrack',
} as const;

/** Úřady Drive picker / attach — existing skeleton leaf, not a second folder. */
export const PROJECT_DRIVE_PERMITS_ROOT_KEY = 'permitsRoot' satisfies ProjectDriveArtifactFolderKey;

/** Dedicated folder for pasportizace uploads under the stavby Drive root. */
export const PROJECT_DRIVE_PASPORTIZACE_KEY = 'pasportizace' satisfies ProjectDriveArtifactFolderKey;

/** Příprava staveb / Pošta — actual message attachments, not C1/C2/TZ filing slots. */
export const PROJECT_DRIVE_POSTA_KEY = 'posta' satisfies ProjectDriveArtifactFolderKey;

/** `drive_managed_node.artifact_type` for one communication’s Drive folder. */
export const PROJECT_DRIVE_AUTHORITY_MESSAGE_FOLDER_ARTIFACT = 'authority_message_folder';

/** `drive_managed_node.artifact_type` for one MIME attachment of a communication. */
export const PROJECT_DRIVE_AUTHORITY_MESSAGE_FILE_ARTIFACT = 'authority_message_attachment';

/** Human-readable child folder under `08_Komunikace` (stable id suffix). */
export function projectDriveAuthorityMessageFolderName(input: {
  messageId: string;
  occurredAt?: string | Date | null;
  subject?: string | null;
}): string {
  const id = input.messageId.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 8) || 'msg';
  let date = 'nodate';
  const raw = input.occurredAt;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    date = raw.toISOString().slice(0, 10);
  } else if (typeof raw === 'string' && raw.trim()) {
    const match = raw.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) date = match[1];
  }
  const subject =
    (input.subject ?? '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'komunikace';
  return `${date}_${subject}_${id}`;
}

export function projectDriveSkeletonEntry(
  key: ProjectDriveArtifactFolderKey,
): { key: ProjectDriveArtifactFolderKey; segments: readonly string[] } | null {
  return PROJECT_DRIVE_SKELETON.find((item) => item.key === key) ?? null;
}

export function projectDriveArtifactLeafName(key: ProjectDriveArtifactFolderKey): string | null {
  const segments = projectDriveSkeletonEntry(key)?.segments;
  if (!segments?.length) return null;
  return segments[segments.length - 1] ?? null;
}

export function projectDriveBranchFolder(
  responsibleBranch: string | null | undefined,
): string | null {
  const key = (responsibleBranch ?? '').trim();
  if ((POLYGON_RESPONSIBLE_BRANCHES as readonly string[]).includes(key)) {
    return PROJECT_DRIVE_BRANCH_FOLDERS[key as PolygonResponsibleBranch];
  }
  return null;
}

export function projectDriveRealizationYear(
  realizationYear: string | null | undefined,
): string | null {
  const year = (realizationYear ?? '').trim();
  const match = year.match(/20\d{2}/);
  return match?.[0] ?? (year || null);
}

export function projectDriveFolderName(kiaCode: string, name: string): string {
  const kia = kiaCode.trim();
  const short = stripLegacyDriveOrderPrefix(name).trim() || kia;
  return `${kia} — ${short}`;
}

/**
 * Odstraní pořadové / evidenční prefixy ze jména složky zakázky
 * (`01_…`, `123_…`, `01 Krupka…`). Rok v nadřazené cestě zůstává mimo název stavby.
 * Skeleton listy (`01_SD`, `02_PD`) sem nepatří — jen název polygonové složky.
 */
export function stripLegacyDriveOrderPrefix(raw: string): string {
  let s = raw.normalize('NFC').trim();
  // Opakovaně: "01_02_Name" → "Name"
  for (let i = 0; i < 3; i++) {
    const next = s.replace(/^\d{1,4}([_\s.\-–—]+)/u, '').trim();
    if (next === s) break;
    s = next;
  }
  return s || raw.normalize('NFC').trim();
}

/**
 * KIA z názvu legacy složky (`01_JETEP2 — Obec`, `FUNOV2_Stachovice`).
 * Preferuje token před pomlčkou / em dash.
 */
export function extractKiaFromLegacyFolderName(raw: string): string | null {
  const cleaned = stripLegacyDriveOrderPrefix(raw);
  const head = cleaned.split(/\s*[—–]\s*/)[0]?.trim() || cleaned;
  const underscored = head.split(/_/)[0]?.trim() || head;
  const candidate = (underscored || head).trim();
  // ProTelco KIA má písmena + číslici (FUNOV2, JETEP2, LARCPU001) — čistý text („Krupka“) není KIA.
  const match = candidate.match(/^([A-Za-z]{2,10}\d{1,4}[A-Za-z]{0,4}\d{0,4})$/);
  if (match) return match[1].toUpperCase();
  const embedded = cleaned.match(/\b([A-Z]{2,8}\d{1,4}[A-Z]{0,3}\d{0,3})\b/i);
  return embedded?.[1]?.toUpperCase() ?? null;
}

/** Lidský název po odstranění KIA a pořadí (`JETEP2 — Krupka` → `Krupka`). */
export function extractDisplayNameFromLegacyFolderName(raw: string): string {
  const cleaned = stripLegacyDriveOrderPrefix(raw);
  const kia = extractKiaFromLegacyFolderName(raw);
  let rest = cleaned;
  if (kia) {
    const re = new RegExp(`^${kia}\\s*[—–\\-_]?\\s*`, 'i');
    rest = cleaned.replace(re, '').trim();
  }
  rest = rest.replace(/^[—–\-_:]+/, '').trim();
  return rest || cleaned;
}

export const PROJECT_DRIVE_COMPANY_PROTELCO = 'ProTelco';
export const PROJECT_DRIVE_COMPANY_FIBRE_BETA = 'Fibre_Beta';
export const PROJECT_DRIVE_COMPANY_EXTERMIS = 'Extermis';

/**
 * First folder on the year drive: `{firma}_{lokalita}`.
 * `ProTelco_Lovosice`, `Fibre_Beta_Lovosice`, `Fibre_Beta_Pardubice`, `Fibre_Beta_Ostrava`.
 * Company comes from the source path or `responsibleBranch`. A ready name (`Fibre_Beta_Ostrava`) is kept.
 * `01_LINIE&VR` is the Lovosice line. Fibre Beta without a city uses Lovosice (its only HR city).
 * The last path segment is the stavba and is not read as a company or a city.
 */
export function projectDriveCompanyLocalityFolder(input: {
  pathSegments?: readonly string[] | null;
  responsibleBranch?: string | null;
}): string {
  const segments = (input.pathSegments ?? [])
    .map((segment) => String(segment).normalize('NFC').trim())
    .filter(Boolean);
  const parents = segments.length > 1 ? segments.slice(0, -1) : segments;
  for (const segment of parents) {
    const ready = normalizeReadyCompanyLocality(segment);
    if (ready) return ready;
  }

  let company: string | null = null;
  let city: string | null = null;
  for (const segment of parents) {
    if (PROJECT_DRIVE_YEAR_SEGMENT_IN_PATH.test(segment)) continue;
    const key = segment.replace(/^\d{1,2}_/u, '').toLocaleLowerCase('cs-CZ');
    if (PATH_SKIP_KEYS.has(key)) continue;
    if (/beta/u.test(key) && /fib(?:re|er)/u.test(key)) {
      company = PROJECT_DRIVE_COMPANY_FIBRE_BETA;
      continue;
    }
    if (key === 'extermis') {
      company = PROJECT_DRIVE_COMPANY_EXTERMIS;
      continue;
    }
    if (key.startsWith('protelco')) {
      company = PROJECT_DRIVE_COMPANY_PROTELCO;
      const parsed = cityFromPathKey(key.replace(/^protelco[_\s]?/u, ''));
      if (parsed) city = parsed;
      continue;
    }
    const parsed = cityFromPathKey(key);
    if (parsed) city = parsed;
  }

  const fromBranch = companyLocalityFromBranch(input.responsibleBranch);
  if (!company && fromBranch.company) company = fromBranch.company;
  if (!city && fromBranch.city) city = fromBranch.city;
  if (company === PROJECT_DRIVE_COMPANY_FIBRE_BETA && !city) city = 'Lovosice';
  if (company === PROJECT_DRIVE_COMPANY_EXTERMIS && !city) city = 'Lovosice';
  if (!company) company = PROJECT_DRIVE_COMPANY_PROTELCO;
  if (!city) city = 'Lovosice';
  return `${company}_${city}`;
}

/** `{firma}_{lokalita}/{stavba}` on the zakázka-year drive. No archive, investor, or year folder. */
export function projectDriveZakazkaFolderPath(input: {
  stavbaFolderName: string;
  pathSegments?: readonly string[] | null;
  responsibleBranch?: string | null;
}): string[] {
  const stavba = stripLegacyDriveOrderPrefix(input.stavbaFolderName).trim() || 'bez-nazvu';
  return [projectDriveCompanyLocalityFolder(input), stavba];
}

const PROJECT_DRIVE_YEAR_SEGMENT_IN_PATH = /^20\d{2}$/;
const PATH_SKIP_KEYS = new Set([
  'zadání',
  'zadani',
  'cetin',
  'archiv',
  'ostatní',
  'ostatni',
]);

function normalizeReadyCompanyLocality(segment: string): string | null {
  const compact = segment.normalize('NFC').trim().replace(/\s+/gu, '_');
  const match = compact.match(/^(ProTelco|Fibre_Beta|Fiber_Beta|Extermis)_(.+)$/u);
  if (!match) return null;
  const company = match[1] === 'Fiber_Beta' ? PROJECT_DRIVE_COMPANY_FIBRE_BETA : match[1];
  const city = titleCaseCs(match[2]);
  return city ? `${company}_${city}` : null;
}

function titleCaseCs(raw: string): string {
  const text = raw.normalize('NFC').trim().replace(/_+/gu, ' ');
  if (!text) return '';
  const lower = text.toLocaleLowerCase('cs-CZ');
  return lower
    .replace(/(^|[\s-])(\p{L})/gu, (_full, sep: string, ch: string) => `${sep}${ch.toLocaleUpperCase('cs-CZ')}`)
    .replace(/\s+/gu, '_');
}

function cityFromPathKey(key: string): string | null {
  if (key === 'lovosice' || key === 'linie' || key === 'linie&vr' || key === 'linie a vr') return 'Lovosice';
  if (key === 'ostrava') return 'Ostrava';
  if (key === 'pardubice') return 'Pardubice';
  return null;
}

function companyLocalityFromBranch(branch: string | null | undefined): { company: string | null; city: string | null } {
  const text = (branch ?? '').normalize('NFC').trim();
  if (/fib(?:re|er)\s*beta/iu.test(text)) {
    return { company: PROJECT_DRIVE_COMPANY_FIBRE_BETA, city: null };
  }
  if (/extermis/iu.test(text)) {
    return { company: PROJECT_DRIVE_COMPANY_EXTERMIS, city: 'Lovosice' };
  }
  const match = text.match(/^ProTelco\s+(.+)$/iu);
  if (match) {
    const city = cityFromPathKey(match[1].toLocaleLowerCase('cs-CZ')) || titleCaseCs(match[1]);
    return { company: PROJECT_DRIVE_COMPANY_PROTELCO, city: city || null };
  }
  return { company: null, city: null };
}

/**
 * Cesta stavby na disku roku zakázky: `{firma}_{lokalita}/{stavba}`.
 * Rok je název disku, ne složka. Bez `90_ARCHIV` a bez investora.
 */
export function projectDriveArchiveBuildingPath(input: {
  kiaCode?: string | null;
  name: string;
  realizationYear: string;
  responsibleBranch?: string | null;
  customerFolder?: string | null;
  pathSegments?: readonly string[] | null;
}): string[] {
  const year = projectDriveRealizationYear(input.realizationYear);
  if (!year) {
    throw new Error('projectDriveArchiveBuildingPath requires realization year');
  }
  const kia = (input.kiaCode ?? '').trim();
  const stavbaFolderName = kia ? projectDriveFolderName(kia, input.name) : input.name;
  return projectDriveZakazkaFolderPath({
    stavbaFolderName,
    pathSegments: input.pathSegments,
    responsibleBranch: input.responsibleBranch,
  });
}

export {
  projectDriveHasIdentity,
  projectDriveMissingIdentityFields,
  resolvePolygonDriveInvestor,
  type ProjectDriveBootstrapIdentity,
} from '../utils/project-drive-polygon-identity.util';

export function projectDriveBuildingPath(input: {
  kiaCode: string;
  name: string;
  realizationYear: string;
  responsibleBranch: string;
  /** Unused in the path. The year drive has no investor level. */
  customerFolder?: string | null;
}): string[] {
  const year = projectDriveRealizationYear(input.realizationYear);
  const branch = projectDriveBranchFolder(input.responsibleBranch);
  if (!year || !branch) {
    throw new Error('projectDriveBuildingPath requires year and responsible branch');
  }
  return projectDriveZakazkaFolderPath({
    stavbaFolderName: projectDriveFolderName(input.kiaCode, input.name),
    responsibleBranch: input.responsibleBranch,
  });
}

export function projectDriveFolderWebUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(folderId)}`;
}

/**
 * Per-cabinet Drive name so several ROZVADEC objects in one polygon do not overwrite
 * the same `D5.xlsx` / `D5.pdf`. Example: `D5-cab1.xlsx`.
 */
export function projectDrivePdDrawingFolderKey(
  code: string,
): ProjectDrivePdDrawingFolderKey | null {
  const normalized = code.trim().toUpperCase();
  if (!(PROJECT_DRIVE_PD_DRAWING_CODES as readonly string[]).includes(normalized)) {
    return null;
  }
  return `pd${normalized}` as ProjectDrivePdDrawingFolderKey;
}

/**
 * First C1–C3 / D1–D5 token in a PD filename (`C2_polohopis_….pdf`, `D4_A.pdf`).
 * Available for future routing — app artifacts are not yet written into these folders.
 */
export function projectDrivePdDrawingCodeFromFileName(
  fileName: string,
): ProjectDrivePdDrawingCode | null {
  const match = fileName
    .trim()
    .toUpperCase()
    .match(/(?:^|[^A-Z0-9])([CD][1-5])(?![A-Z0-9])/);
  const code = match?.[1];
  if (!code || !(PROJECT_DRIVE_PD_DRAWING_CODES as readonly string[]).includes(code)) {
    return null;
  }
  return code as ProjectDrivePdDrawingCode;
}

export function projectDriveOccupancySchemaFileName(
  file:
    | typeof PROJECT_DRIVE_OCCUPANCY_SCHEMA_XLSX
    | typeof PROJECT_DRIVE_OCCUPANCY_SCHEMA_PDF,
  objectId: string,
): string {
  const safe = objectId.replace(/[^a-zA-Z0-9_-]/g, '') || 'rozvadec';
  const dot = file.lastIndexOf('.');
  const stem = dot >= 0 ? file.slice(0, dot) : file;
  const ext = dot >= 0 ? file.slice(dot) : '';
  return `${stem}-${safe}${ext}`;
}

export function diaryDriveDateSegments(entryDate: string): string[] {
  const iso = entryDate.trim();
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return [iso];
  return [match[1], match[2], iso.slice(0, 10)];
}
